"use client";

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, MessageCircle, Camera, Play, Gamepad, Mail, Scan, Cloudy, Check, CircleHelp, CircleFadingPlusIcon } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const setRef = (index: number) => (el: HTMLDivElement | null) => {
    contentRefs.current[index] = el;
  };

  const faqs = [
    {
      question: "What does Saved do?",
      answer: "Saved uses agentic AI and fine-tuned machine learning models to detect and clip the best parts of your VODs.",
      icon: <Check className="w-5 h-5 text-gray-400" />
    },
    {
      question: "How does it work?",
      answer: "Saved watches your live stream and clips the best parts in real-time, adding animated subtitles, and cropping the background. Saved optionally posts these clips to your stream chat and Discord server.",
      icon: <CircleHelp className="w-5 h-5 text-gray-400" />
    },
    {
      question: "What platforms does Saved support?",
      answer: "For live streams, we support Twitch and Kick. For VODs, we support Twitch, Kick, YouTube, and file uploads.",
      icon: <CircleFadingPlusIcon className="w-5 h-5 text-gray-400" />
    },
    {
      question: "How accurate is your highlight detection?",
      answer: "We benchmark our bot against other clipping tools: we can detect ~60% of stream highlights while other bots only detect ~8%. This number is constantly improving.",
      icon: <Scan className="w-5 h-5 text-gray-400" />
    },
    {
      question: "Do I need to install anything?",
      answer: "No, you can use our service directly from your browser. Everything is handled on our servers.",
      icon: <Cloudy className="w-5 h-5 text-gray-400" />
    },
    {
      question: "Do my videos need a facecam?",
      answer: "Nope! You can convert your VODs to have a blurred background instead to maintain the vertical aspect ratio.",
      icon: <Camera className="w-5 h-5 text-gray-400" />
    },
    {
      question: "Does the free tier have low quality?",
      answer: "All users get the 1080p clips",
      icon: <Play className="w-5 h-5 text-gray-400" />
    },
    {
      question: "Why is this better than Twitch's Clipping Feature?",
      answer: "We automatically find the best parts of your VOD and add animated subtitles - Twitch does neither.",
      icon: <MessageCircle className="w-5 h-5 text-gray-400" />
    },
    {
      question: "What kind of VODs work best?",
      answer: "Saved works for any type of content, from mainstream games to music, and even niche content.",
      icon: <Gamepad className="w-5 h-5 text-gray-400" />
    },
    {
      question: "Can't find the answer you're looking for?",
      answer: "Join our Discord server! or email aayush@saved.gg",
      icon: <Mail className="w-5 h-5 text-gray-400" />
    },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      {/* <h2 className="text-4xl font-bold text-white mb-12">Frequently Asked Questions</h2> */}
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="border border-border hover:border-border/80 transition-colors duration-200"
          >
            <button
              className="w-full text-left px-6 py-4 focus:outline-none"
              onClick={() => handleToggle(index)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {faq.icon}
                  <h3 className="text-lg font-medium text-foreground">{faq.question}</h3>
                </div>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </button>
            <div
              ref={setRef(index)}
              className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
                openIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="px-6 pb-4 pt-2 text-muted-foreground border-t border-border">
                {faq.answer}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQ;